import React, { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { setNavPanelWidth } from '../../actions/mainUI';

function NavContent({ isNavExpanded, selectedComponent, onCollapse, isWorkspaceExpanded }) {
  const dispatch = useDispatch();
  const navPanelWidth = useSelector(state => state.mainUI.get('navPanelWidth'));
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = navPanelWidth;
    e.preventDefault();
    
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      
      const deltaX = e.clientX - startX.current; // Normal direction: right = increase width, left = decrease width
      const newWidth = Math.max(200, Math.min(600, startWidth.current + deltaX)); // Min 200px, Max 600px
      
      // Update CSS custom property immediately for smooth dragging
      document.documentElement.style.setProperty('--nav-panel-width', `${newWidth}px`);
    };
    
    const handleMouseUp = () => {
      if (!isDragging.current) return;
      
      isDragging.current = false;
      
      // Get the final width from the CSS custom property and update the store
      const finalWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-panel-width'), 10);
      dispatch(setNavPanelWidth(finalWidth));
      
      // Remove event listeners
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [navPanelWidth, dispatch]);

  // Update CSS custom property when navPanelWidth changes
  useEffect(() => {
    document.documentElement.style.setProperty('--nav-panel-width', `${navPanelWidth}px`);
  }, [navPanelWidth]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.removeEventListener('mousemove', () => {});
        document.removeEventListener('mouseup', () => {});
      }
    };
  }, []);

  return (
    <div
      className={
        classNames(
          'nav-content',
          { 'nav-content--open': isNavExpanded },
          { 'nav-content--workspace-open': isWorkspaceExpanded }
        )
      }
    >
      {isNavExpanded && (
        <div 
          className="nav-content__resize-handle"
          onMouseDown={handleMouseDown}
        />
      )}
      <i onClick={onCollapse} className="nav-content__back-icon" />
      {selectedComponent}
    </div>
  );
}

NavContent.propTypes = {
  isNavExpanded: PropTypes.bool,
  selectedComponent: PropTypes.element,
  onCollapse: PropTypes.func,
  isWorkspaceExpanded: PropTypes.bool,
};

export default NavContent;
